import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, ButtonBase, Button } from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import { fetchBookingAnalytics } from "../api/analytics";
import BookingAnalytics from "./BookingAnalytics";
import EmptyState from "../../../shared/ui/EmptyState";

const periods = [
  { value: "month", label: "Monthly" },
  { value: "week", label: "Weekly" },
];

const BookingKPIWidget = ({ ownerId, distributorId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState("month");

  const loadAnalytics = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchBookingAnalytics({ ownerId, distributorId, months: 6, period });
      setAnalytics(data);
    } catch (err) {
      console.error("Error loading booking analytics:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAnalytics(); }, [ownerId, distributorId, period]);

  const isEmpty =
    analytics &&
    analytics.totalBookings === 0 &&
    analytics.initiated?.every((v) => v === 0) &&
    analytics.paid?.every((v) => v === 0) &&
    analytics.cancelled?.every((v) => v === 0);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Analytics Overview
        </Typography>

        <Box sx={{ display: "flex", gap: 0.5, p: 0.5, bgcolor: "#F0F0F5", borderRadius: 2 }}>
          {periods.map((p) => (
            <ButtonBase
              key={p.value}
              onClick={() => setPeriod(p.value)}
              sx={{
                px: 1.75,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: "0.8rem",
                fontWeight: 600,
                transition: "all 0.15s",
                color: period === p.value ? "#FFFFFF" : "text.secondary",
                bgcolor: period === p.value ? "primary.main" : "transparent",
                "&:hover": {
                  bgcolor: period === p.value ? "primary.dark" : "rgba(0,0,0,0.06)",
                },
              }}
            >
              {p.label}
            </ButtonBase>
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
          <CircularProgress color="primary" size={32} />
        </Box>
      ) : error ? (
        <EmptyState
          icon={<BarChartOutlinedIcon />}
          title="Couldn't load analytics"
          description="There was a problem fetching your analytics data. Check your connection and try again."
          action={
            <Button variant="outlined" size="small" onClick={loadAnalytics}>
              Retry
            </Button>
          }
          py={8}
        />
      ) : isEmpty ? (
        <EmptyState
          icon={<BarChartOutlinedIcon />}
          title="No activity yet"
          description="Your booking analytics will appear here once you receive your first booking. Stats update in real time."
          py={8}
        />
      ) : analytics ? (
        <BookingAnalytics
          totalBookings={analytics.totalBookings}
          percentageChange={analytics.percentageChange}
          labels={analytics.labels}
          initiated={analytics.initiated}
          paid={analytics.paid}
          cancelled={analytics.cancelled}
        />
      ) : null}
    </Box>
  );
};

export default BookingKPIWidget;
